/**
 * Full-size image preview — see the image before committing to it.
 *
 * Operator 2026-08-13: "If I can't see a full size view of it without
 * selecting it, I can't tell if it's the one I want." Every grid in the
 * manager renders originals scaled into small tiles; this shows one at its
 * real size, and arrows through the rest of the same grid, without touching
 * whatever the tile's own click does (select, open, replace).
 *
 * Escape is captured, not bubbled: the picker and the shared Modal both close
 * on a document-level Escape, and a preview opened inside one of them must
 * close ONLY itself. Capture-phase + stopPropagation means the host dialog
 * never sees the key.
 */
import { useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, X } from 'lucide-react';

export interface LightboxItem {
  /** Full-size URL — the original, not a thumbnail variant. */
  url: string;
  alt?: string | null;
  title?: string | null;
  /** Small print under the title: role · type · quality, whatever fits. */
  subtitle?: string | null;
}

export function ImageLightbox({ items, index, onIndex, onClose }: {
  items: LightboxItem[];
  index: number;
  onIndex: (next: number) => void;
  onClose: () => void;
}) {
  const count = items.length;
  const item = items[index];

  const step = useCallback((delta: number) => {
    if (count < 2) return;
    onIndex((index + delta + count) % count);
  }, [count, index, onIndex]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Stop the host dialog's own Escape handler from firing too.
        e.stopPropagation();
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowRight') {
        e.stopPropagation();
        step(1);
      } else if (e.key === 'ArrowLeft') {
        e.stopPropagation();
        step(-1);
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [onClose, step]);

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/90 flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
      onClick={onClose}
    >
      <div
        className="flex items-start justify-between gap-4 p-4 text-white bg-black/60 backdrop-blur-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0">
          {item.title && <p className="text-sm font-medium truncate">{item.title}</p>}
          {item.subtitle && <p className="text-xs text-white/60 truncate">{item.subtitle}</p>}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {count > 1 && (
            <span className="text-xs text-white/60 tabular-nums">{index + 1} / {count}</span>
          )}
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-white"
            title="Open the original in a new tab"
          >
            <ExternalLink className="h-4 w-4" /> Original
          </a>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/10 text-white"
            aria-label="Close preview"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex items-center gap-2 px-2 pb-4">
        {count > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); step(-1); }}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white shrink-0"
            aria-label="Previous image"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        <img
          src={item.url}
          alt={item.alt ?? ''}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 min-w-0 max-h-full object-contain"
        />
        {count > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); step(1); }}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white shrink-0"
            aria-label="Next image"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The magnifier that opens the preview. Rendered as a SIBLING overlaying a
 * tile, never nested inside it — the tiles are <button>s (select / open) and
 * a button inside a button is invalid HTML whose clicks reach both handlers.
 */
export function ZoomButton({ onClick, className }: {
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title="Preview full size (does not select)"
      aria-label="Preview full size"
      className={
        'absolute top-1.5 right-1.5 z-10 p-1.5 rounded-md bg-black/55 text-white ' +
        'opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-black/75 ' +
        'transition-opacity max-sm:opacity-100 ' + (className ?? '')
      }
    >
      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5M11 8v6M8 11h6" />
      </svg>
    </button>
  );
}
